import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import * as Yup from 'yup';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import cn from 'classnames';
import { Formik, Form, Field } from 'formik';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../src/i18n';
import { TP } from '../../constants/consts';
import { colors } from '../../constants/styleConstants';
import CircleButton from '../CircleButton';
import SimpleModal from '../Modal';
import Button from '../Button';
import TextField from '../Fields/TextField';
import TextButton from '../TextButton';
import * as API from '../../utils/API';
import endpointConstants from '../../constants/endpointConstants';
import { selectActiveProfile } from '../../containers/App/selectors';

const useStyles = makeStyles(() => ({
  icon: {
    color: colors.trlBlue,
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
    display: 'inline-block',
    padding: 4,
    paddingBottom: 0,
    border: 'solid 1px #ebebeb',
    borderRadius: '50%',
    '& svg': {
      width: 18,
      height: 10,
    },
  },
  share: {
    display: 'inline-block',
  },

  modal: {
    maxWidth: 450,
    padding: '20px 15px 30px',
    borderRadius: 5,
  },
  title: {
    fontFamily: 'PlayfairDisplay',
    fontSize: 26,
    letterSpacing: 0,
    color: colors.secondaryTitleColor,
  },
  subTitle: {
    fontFamily: 'Roboto',
    fontSize: 13,
    letterSpacing: 0,
    color: '#575757',
  },
  field: {
    marginTop: 10,
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 10,
    marginRight: 20,
  },
  errorMessage: {
    color: colors.red,
    textAlign: 'center',
    marginTop: 10,
  },
  continueBtn: {
    marginTop: 20,
  },
  submitedSubtitle: {
    fontSize: 16,
  },
  center: {
    textAlign: 'center',
  },
}));

const shareValidation = Yup.object().shape({
  firstName: Yup.string().required(`${TP}.m_LOGIN_REQUIRED`),
  lastName: Yup.string().required(`${TP}.m_LOGIN_REQUIRED`),
  email: Yup.string()
    .email(`${TP}.m_MAILNONVALID`)
    .required(`${TP}.m_LOGIN_REQUIRED`),
  confirmEmail: Yup.string()
    .oneOf([Yup.ref(`email`)], `${TP}.m_LOGIN_EMAILSNOTMATCHED`)
    .required(`${TP}.m_LOGIN_REQUIRED`),
});

const ShareAccess = ({ className, modalClassName, entityType = 'profile', entity }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isModalOpen, setModalOpen] = useState(false);
  const [submitCompletedEmail, setSubmitCompletedEmail] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const activeProfile = useSelector(selectActiveProfile());
  const targetEntity = entity || activeProfile;

  const shareAccess = (values, setSubmitting) => {
    setErrorMessage(null);
    const requestData = {
      user: { firstName: values.firstName, lastName: values.lastName, email: values.email },
    };

    if (entityType === 'profile') {
      requestData.accessType = 'profile';
      requestData.profile = targetEntity;
    }
    if (entityType === 'agency') {
      requestData.accessType = 'agency';
      requestData.agency = targetEntity;
    }
    if (entityType === 'company') {
      requestData.accessType = 'company';
      requestData.company = targetEntity;
    }

    API.create(endpointConstants.PROFILE_ACCESSES, requestData)
      .then(() => setSubmitCompletedEmail(values.email))
      .catch(error => setErrorMessage(error.message))
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <CircleButton
        className={className}
        icon={<GroupAddIcon style={{ color: colors.trlBlue }} />}
        text={t(`${TP}.FN_SHARE_ACCESS`)}
        onClick={() => setModalOpen(true)}
      />
      <SimpleModal
        isModalOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        hideCloseIcon
        className={cn(classes.modal, modalClassName, { [classes.center]: !!submitCompletedEmail })}
      >
        <h3 className={classes.title}>
          {submitCompletedEmail ? t(`${TP}.FN_ACCESS_SHARED`) : t(`${TP}.FN_SHARE_ACCESS`)}
        </h3>
        <h4 className={cn(classes.subTitle, { [classes.submitedSubtitle]: !!submitCompletedEmail })}>
          {submitCompletedEmail
            ? t(`${TP}.FN_ACCESS_SHARED_TEXT`, { email: submitCompletedEmail })
            : t(`${TP}.FN_ACCESS_SHARE_TEXT`)}
        </h4>
        {submitCompletedEmail ? (
          <Button
            className={classes.continueBtn}
            onClick={() => {
              setModalOpen(false);
              setSubmitCompletedEmail(null);
            }}
          >
            {t(`${TP}.m_CONTINUE`)}
          </Button>
        ) : (
          <Formik
            enableReinitialize
            validationSchema={shareValidation}
            initialValues={{ firstName: '', lastName: '', email: '', confirmEmail: '' }}
            onSubmit={(values, { setSubmitting }) => shareAccess(values, setSubmitting)}
          >
            {({ isSubmitting }) => (
              <Form className={classes.form} autoComplete="off">
                <Field
                  name="firstName"
                  required
                  className={classes.field}
                  placeholder={t(`${TP}.m_FIRSTNAME`)}
                  label={t(`${TP}.m_FIRSTNAME`)}
                  component={TextField}
                />
                <Field
                  name="lastName"
                  required
                  className={classes.field}
                  placeholder={t(`${TP}.m_REGISTRATION_LASTNAME`)}
                  label={t(`${TP}.m_REGISTRATION_LASTNAME`)}
                  component={TextField}
                />
                <Field
                  name="email"
                  required
                  className={classes.field}
                  placeholder={t(`${TP}.m_EMAIL`)}
                  label={t(`${TP}.m_EMAIL`)}
                  component={TextField}
                />
                <Field
                  name="confirmEmail"
                  required
                  className={classes.field}
                  placeholder={t(`${TP}.m_REGISTRATION_CONFIRMEMAIL`)}
                  label={t(`${TP}.m_REGISTRATION_CONFIRMEMAIL`)}
                  component={TextField}
                />
                <div className={classes.errorMessage}>{errorMessage}</div>
                <div className={classes.buttons}>
                  <Button type="submit" className={classes.button} disabled={isSubmitting}>
                    {t(`${TP}.m_SOCIAL_SHARE`)}
                  </Button>
                  <TextButton type="button" className={classes.button} onClick={() => setModalOpen(false)}>
                    {t(`${TP}.m_CANCEL`)}
                  </TextButton>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </SimpleModal>
    </>
  );
};

ShareAccess.propTypes = {
  className: PropTypes.string,
};

export default memo(ShareAccess);
